import { EMAIL_VALIDATION_REGEX } from '../constants/shell';
import {
  ICreateSendRecipient,
  IEffectiveShipping,
  ShippingModeEnum,
  TAddressMode,
  TClaimLinkMode,
  TMultiKitAddressMode,
} from '../types/createSendRecipients';
import { ICommonAddress } from '../types/shipping';
import { isUnknownState } from './addressValidation';
import { isStateRequired } from './country';

const hasText = (value?: string): boolean => Boolean(value && value.trim());

export const isValidEmail = (email: string): boolean => new RegExp(EMAIL_VALIDATION_REGEX).test(email.trim());

export const resolveEffectiveShipping = (
  isMultiKit: boolean,
  addressMode: TAddressMode,
  claimLinkMode: TClaimLinkMode,
  multiKitMode: TMultiKitAddressMode,
  isClaimForced: boolean,
  isContentDigital: boolean = false,
): IEffectiveShipping => {
  if (isMultiKit || isClaimForced) {
    return { isMultiKit, mode: multiKitMode, isClaimBased: true };
  }
  if (!isContentDigital && addressMode === ShippingModeEnum.ClaimLink) {
    return { isMultiKit, mode: claimLinkMode, isClaimBased: true };
  }
  return { isMultiKit, mode: ShippingModeEnum.YouProvide, isClaimBased: false };
};

const isShippingAddressComplete = (address: ICommonAddress | null): boolean => {
  if (!address) {
    return false;
  }

  return (
    hasText(address.country) &&
    hasText(address.address1) &&
    hasText(address.city) &&
    hasText(address.zip) &&
    (!isStateRequired(address.country) || hasText(address.state)) &&
    !isUnknownState(address.state, address.country)
  );
};

export interface IRecipientCompletenessContext {
  addressMode: TAddressMode;
  hasOverride: boolean;
  isAddressApplicable?: boolean;
}

export const isRecipientComplete = (recipient: ICreateSendRecipient): boolean =>
  hasText(recipient.first_name) &&
  hasText(recipient.last_name) &&
  hasText(recipient.email) &&
  isValidEmail(recipient.email);

export const isRecipientMissingContact = (recipient: ICreateSendRecipient): boolean =>
  !hasText(recipient.first_name) || !hasText(recipient.last_name) || !hasText(recipient.email);

export const isRecipientMissingShippingAddress = (
  recipient: ICreateSendRecipient,
  { addressMode, hasOverride, isAddressApplicable = true }: IRecipientCompletenessContext,
): boolean =>
  isAddressApplicable &&
  addressMode === ShippingModeEnum.YouProvide &&
  !hasOverride &&
  !isShippingAddressComplete(recipient.address);

export const isRecipientPhoneRequired = (isContentDigital: boolean, isClaimBased: boolean): boolean =>
  !isContentDigital && !isClaimBased;

export const isRecipientMissingPhone = (recipient: ICreateSendRecipient, isPhoneRequired: boolean): boolean =>
  isPhoneRequired && !hasText(recipient.phone);
